<template>
    <div v-show="stepActive" class="tournament-participants">
        <h2 v-if="!hideMeta" class="title">{{ $t('title_ParticipantSettings') }}</h2>
        <p v-if="!hideMeta" class="subtitle">{{ $t('subtitle_ParticipantSettings') }}</p>

        <div class="info-card">
            <v-form>
                <div class="section">
                    <h3 class="section-title m-bttom d-flex align-center">
                        {{ $t('ranking.title') }}
                        <v-btn
                            icon
                            small
                            class="ml-2"
                            style="width: 0; height: 0; padding: 8px 8px; margin-top: 4px; background-color: #9a9a9a;"
                            @click="showRankingModal = true">
                            <v-icon style="font-size: 11px; font-weight: bold; color: white !important;">fa-question</v-icon>
                        </v-btn>
                    </h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1" style="margin-top: 0;">{{$t('ranking.description') }}</p>
                    <v-select
                        v-if="availableRankingTypes.length > 0"
                        v-model="rankingType"
                        :items="availableRankingTypes"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        append-icon="fa-regular fa-chevron-down"
                        @change="validateForm">
                    </v-select>
                    <v-text-field
                        v-else
                        :value="noRankingsMessage"
                        outlined
                        dense
                        readonly
                        disabled
                        :hint="$t('ranking.contactSupport') || 'Please contact Swiset support for assistance'"
                        persistent-hint>
                    </v-text-field>
                </div>
                <div class="section">
                    <h3 class="section-title mb-0">{{ $t('registration.title') }}</h3>
                    <p class="subtitle-2 text--disabled mb-4 mt-1" style="margin-top: 0;">{{
                        $t('registration.description') }}</p>
                    <div v-if="tournamentDurationInDays > 0" class="registration-timeline-editor">
                        <div
                            ref="timelineTrack"
                            class="timeline-container timeline-container--interactive"
                            @click="onTimelineClick">
                            <div class="timeline">
                                <div class="timeline-base"></div>
                                <div class="tournament-period" :style="{ width: '100%' }">
                                    <!-- <span class="period-label tournament">{{
                                        $t('registration.timeline.tournament')
                                    }}</span> -->
                                </div>
                                <div
                                    v-if="registrationDays > 0"
                                    class="registration-period"
                                    :style="{ width: `${getRegistrationWidth}%` }">
                                    <span class="period-label registration">{{
                                        $t('registration.timeline.registration')
                                    }}</span>
                                </div>
                                <div class="timeline-markers">
                                    <div class="marker start">
                                        <div class="date">{{ formattedStartDate }}</div>
                                        <div class="label">{{ $t('registration.timeline.tournamentStart') }}</div>
                                    </div>
                                    <div
                                        class="marker registration-end registration-handle"
                                        :class="{ 'registration-handle--dragging': isDraggingRegistration, 'registration-handle--closed': registrationDays <= 0 }"
                                        :style="{ left: `${getRegistrationWidth}%` }"
                                        role="slider"
                                        :aria-valuemin="0"
                                        :aria-valuemax="tournamentDurationInDays"
                                        :aria-valuenow="registrationDays"
                                        :aria-label="$t('registration.title')"
                                        tabindex="0"
                                        @mousedown.prevent="startRegistrationDrag"
                                        @touchstart.prevent="startRegistrationDrag"
                                        @keydown.left.prevent="nudgeRegistrationDays(-1)"
                                        @keydown.right.prevent="nudgeRegistrationDays(1)"
                                        @keydown.home.prevent="setRegistrationDays(0)"
                                        @keydown.end.prevent="setRegistrationDays(tournamentDurationInDays)">
                                        <div v-if="registrationDays > 0" class="date registration-end-date">{{ formattedRegistrationEndDate }}</div>
                                        <div class="registration-handle__knob"></div>
                                    </div>
                                    <div class="marker end">
                                        <div class="date end-date">{{ formattedEndDate }}</div>
                                        <div class="label">{{ $t('registration.timeline.tournamentEnd') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p class="registration-timeline-editor__summary">{{ registrationSummary }}</p>
                    </div>
                    <p v-else class="subtitle-2 text--disabled">{{ $t('registration.slider.noDates') }}</p>
                </div>
                <div class="section mb-0">
                    <div>
                        <h3 class="section-title mb-0">{{ $t('inviteOnly.title') }}</h3>
                    </div>
                    <div class="d-flex justify-space-between">
                        <p class="subtitle-2 text--disabled mt-1">{{ $t('inviteOnly_ParticipantSettings.description') }}</p>
                        <v-switch v-model="inviteOnly" style="margin-top: -2px;"></v-switch>
                    </div>
                </div>
            </v-form>
        </div>

        <!-- Ranking Types Modal -->
        <v-dialog v-model="showRankingModal" max-width="900px" content-class="ranking-modal">
            <v-card>
                <v-card-title class="ranking-modal-header">
                    <h3>{{ $t('ranking.modal.title') }}</h3>
                    <v-btn icon @click="showRankingModal = false">
                        <v-icon>fa-times</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text class="ranking-modal-content">
                    <p class="modal-description">{{ $t('ranking.modal.description') }}</p>
                    <div class="ranking-table-wrapper">
                        <table class="ranking-comparison-table">
                            <thead>
                                <tr>
                                    <th>{{ $t('ranking.modal.table.rankingType') }}</th>
                                    <th>{{ $t('ranking.modal.table.description') }}</th>
                                    <th>{{ $t('ranking.modal.table.formula') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="ranking in allRankingTooltips" :key="ranking.code">
                                    <td class="ranking-name">{{ ranking.title }}</td>
                                    <td class="ranking-description">{{ ranking.description }}</td>
                                    <td class="ranking-formula">
                                        <div v-if="typeof ranking.formula === 'string'" class="formula-single">
                                            {{ ranking.formula }}
                                        </div>
                                        <div v-else class="formula-dual">
                                            <div><strong>Demo:</strong> {{ ranking.formula.demo }}</div>
                                            <div><strong>Live:</strong> {{ ranking.formula.live }}</div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import TournamentRepository from '@/repositories/tournament';

export default {
    name: 'ParticipantSettings',
    props: {
        stepActive: {
            type: Boolean,
            required: true
        },
        setNextBtnDisabled: {
            type: Function,
            required: true
        },
        clubId: {
            type: Number,
            required: false
        },
        hideMeta: {
            type: Boolean,
            default: false
        },
    },
    data: () => ({
        showRankingModal: false,
        isDraggingRegistration: false,
        availableRankings: [], // Start with empty array
        loading: false,
        noRankingsMessage: 'No ranking types available',
        // All possible ranking types
        allRankingCodes: [
            'SWISET_RATIO',
            'PROFIT_PERCENTAGE',
            'EQUITY_PERCENTAGE',
            'PROFIT_FACTOR',
            'AVG_WIN_LOSS_RATIO',
            'EQUITY_RT',
            'EQUITY_ROI_PERCENTAGE',
            'BALANCE',
            'PROFIT'
        ]
    }),
    computed: {
        ...mapState({
            formData: state => state.tournamentForm.participants,
            basicInfo: state => state.tournamentForm.basicInfo // Add this line
        }),
        // Account type is now determined by tournament type in TournamentDetails
        accountType() {
            // Get tournament type from details form
            const tournamentType = this.$store.state.tournamentForm.details.tournamentType;
            // Map tournament type to account type (DEMO stays DEMO, LIVE becomes LIVE)
            return tournamentType || 'ALL';
        },
        registrationDays: {
            get() {
                if (this.formData.registrationDays !== undefined && this.formData.registrationDays !== null) {
                    return this.formData.registrationDays;
                }
                return this.daysFromRegistrationPeriod(this.formData.registrationPeriod);
            },
            set(value) {
                this.setRegistrationDays(value);
            }
        },
        inviteOnly: {
            get() { return this.formData.inviteOnly; },
            set(value) {
                this.updateTournamentParticipants({
                    ...this.formData,
                    inviteOnly: value
                });
            }
        },
        rankingType: {
            get() { return this.formData.rankingType; },
            set(value) {
                this.updateTournamentParticipants({
                    ...this.formData,
                    rankingType: value
                });
            }
        },
        availableRankingTypes() {
            // Only use rankings from API
            if (this.availableRankings && this.availableRankings.length > 0) {
                return this.availableRankings.map(ranking => ({
                    text: ranking.name,
                    value: ranking.code
                }));
            }
            // If API returned empty or nothing loaded, show no options
            return [];
        },
        // Add new computed property
        tournamentDurationInDays() {
            if (!this.basicInfo.startDate || !this.basicInfo.endDate) return 0;
            const start = new Date(this.basicInfo.startDate);
            const end = new Date(this.basicInfo.endDate);
            return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        },
        // Modify availableRegistrationPeriods
        formattedStartDate() {
            return this.basicInfo.startDate ? new Date(this.basicInfo.startDate).toLocaleDateString() : '';
        },
        formattedEndDate() {
            return this.basicInfo.endDate ? new Date(this.basicInfo.endDate).toLocaleDateString() : '';
        },
        formattedRegistrationEndDate() {
            if (!this.basicInfo.startDate || this.registrationDays <= 0) return '';
            const start = new Date(this.basicInfo.startDate);
            const regEnd = new Date(start);
            regEnd.setDate(start.getDate() + this.registrationDays);
            return regEnd.toLocaleDateString();
        },
        getRegistrationWidth() {
            if (this.registrationDays <= 0 || this.tournamentDurationInDays <= 0) return 0;
            return Math.min(100, (this.registrationDays / this.tournamentDurationInDays) * 100);
        },
        registrationSummary() {
            const days = this.registrationDays;
            if (days <= 0) {
                return this.$t('registration.slider.closed');
            }
            if (days >= this.tournamentDurationInDays) {
                return this.$t('registration.slider.openUntilEnd');
            }
            return this.$t('registration.slider.summary', {
                date: this.formattedRegistrationEndDate,
                days
            });
        },
        platformSelected() {
            const platform = this.$store.state.tournamentForm.details.platform;
            return platform && platform.version && platform.version !== 'None';
        },
        allRankingTooltips() {
            return this.allRankingCodes.map(code => {
                const tooltip = this.getRankingTooltip(code);
                return {
                    code,
                    ...tooltip
                };
            });
        }
    },
    watch: {
        rankingType: {
            handler(newVal, oldVal) {
                if (oldVal && newVal !== oldVal) {
                    // Reset rules when ranking type changes
                    this.updateTournamentRules([]);
                }
            }
        },
        tournamentDurationInDays: {
            handler(newDuration) {
                this.syncRegistrationDaysToTournamentDuration(newDuration);
            },
            immediate: true
        },
        stepActive(isActive) {
            if (isActive) {
                this.syncRegistrationDaysToTournamentDuration();
            }
        }
        // Remove platformSelected watcher as account type is now handled in TournamentDetails
    },
    methods: {
        ...mapMutations(['updateTournamentRules', 'updateTournamentParticipants']),

        getRankingTooltip(rankingCode) {
            const tooltipKey = `ranking.tooltips.${rankingCode}`;
            const tooltip = this.$t(tooltipKey);

            // Fallback in case translation doesn't exist
            if (tooltip === tooltipKey || !tooltip.title) {
                return {
                    title: rankingCode,
                    description: 'No description available',
                    formula: 'N/A'
                };
            }

            return tooltip;
        },

        async fetchAvailableRankings() {
            const platformInfo = this.$store.state.tournamentPlatformInfo;
            // Use selectedClubId from form state first (broker app selection), then clubMember, then prop
            const clubId = this.$store.state.tournamentForm?.basicInfo?.selectedClubId
                || this.$store.state?.clubMember?.club?.id
                || this.clubId;

            // Use either mtGroupId or platformGroupId, whichever is available
            const groupId = platformInfo.mtGroupId || platformInfo.platformGroupId;

            if (platformInfo.platform) {
                this.loading = true;
                try {
                    const response = await TournamentRepository.getWizardRankings(
                        clubId,
                        platformInfo.platform,
                        groupId
                    );
                    this.availableRankings = response.data || [];

                    // Reset ranking type if current selection is not available
                    if (this.rankingType && !this.availableRankings.find(r => r.code === this.rankingType)) {
                        this.rankingType = this.availableRankings.length > 0 ? this.availableRankings[0].code : '';
                    }
                    // Set first available ranking if none selected
                    if (!this.rankingType && this.availableRankings.length > 0) {
                        this.rankingType = this.availableRankings[0].code;
                    }
                } catch (error) {
                    /* eslint-disable no-console */
                    console.error('Error fetching available rankings:', error);
                    /* eslint-enable no-console */
                    // Fall back to default rankings
                    this.availableRankings = [];
                } finally {
                    this.loading = false;
                }
            }
        },

        validateForm() {
            // If no ranking types available, don't allow proceeding
            if (this.availableRankingTypes.length === 0) {
                this.setNextBtnDisabled(true);
                return;
            }
            const isValid = this.rankingType && this.registrationDays !== null && this.registrationDays !== undefined;
            this.setNextBtnDisabled(!isValid);
        },

        daysFromRegistrationPeriod(period) {
            switch (period) {
                case 'ONE_DAY': return 1;
                case 'THREE_DAYS': return 3;
                case 'FIVE_DAYS': return 5;
                case 'ALWAYS': return this.tournamentDurationInDays;
                default: return 0;
            }
        },

        registrationPeriodFromDays(days) {
            if (days <= 0) return 'NOT_ALLOWED';
            if (days >= this.tournamentDurationInDays) return 'ALWAYS';
            if (days === 1) return 'ONE_DAY';
            if (days === 3) return 'THREE_DAYS';
            if (days === 5) return 'FIVE_DAYS';
            return 'ALWAYS';
        },

        setRegistrationDays(days) {
            const maxDays = this.tournamentDurationInDays;
            let clampedDays = Math.max(0, Math.round(days));
            if (maxDays > 0) {
                clampedDays = Math.min(maxDays, clampedDays);
            }
            this.updateTournamentParticipants({
                ...this.formData,
                registrationDays: clampedDays,
                registrationPeriod: this.registrationPeriodFromDays(clampedDays)
            });
        },

        syncRegistrationDaysToTournamentDuration(duration = this.tournamentDurationInDays) {
            if (duration > 0 && this.registrationDays > duration) {
                this.setRegistrationDays(duration);
            }
        },

        nudgeRegistrationDays(delta) {
            this.setRegistrationDays(this.registrationDays + delta);
            this.validateForm();
        },

        startRegistrationDrag() {
            this.isDraggingRegistration = true;
            document.addEventListener('mousemove', this.onRegistrationDrag);
            document.addEventListener('mouseup', this.stopRegistrationDrag);
            document.addEventListener('touchmove', this.onRegistrationDrag, { passive: false });
            document.addEventListener('touchend', this.stopRegistrationDrag);
        },

        stopRegistrationDrag() {
            if (!this.isDraggingRegistration) return;
            this.isDraggingRegistration = false;
            document.removeEventListener('mousemove', this.onRegistrationDrag);
            document.removeEventListener('mouseup', this.stopRegistrationDrag);
            document.removeEventListener('touchmove', this.onRegistrationDrag);
            document.removeEventListener('touchend', this.stopRegistrationDrag);
            this.validateForm();
        },

        onRegistrationDrag(event) {
            if (!this.isDraggingRegistration) return;
            event.preventDefault();
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            this.setRegistrationFromClientX(clientX);
        },

        onTimelineClick(event) {
            if (event.target.closest('.registration-handle')) return;
            this.setRegistrationFromClientX(event.clientX);
            this.validateForm();
        },

        setRegistrationFromClientX(clientX) {
            const track = this.$refs.timelineTrack;
            if (!track || this.tournamentDurationInDays <= 0) return;
            const rect = track.getBoundingClientRect();
            const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            this.setRegistrationDays(Math.round(percent * this.tournamentDurationInDays));
        }
    },
    beforeDestroy() {
        this.stopRegistrationDrag();
    },
    async created() {
        if (this.formData.registrationDays === undefined || this.formData.registrationDays === null) {
            this.setRegistrationDays(this.daysFromRegistrationPeriod(this.formData.registrationPeriod));
        } else {
            this.syncRegistrationDaysToTournamentDuration();
        }

        // Fetch available rankings based on platform
        await this.fetchAvailableRankings();

        // Don't set a default - let it come from API
        this.validateForm();

        // Watch for platform info changes
        this.$watch(
            () => this.$store.state.tournamentPlatformInfo,
            async () => {
                await this.fetchAvailableRankings();
            },
            { deep: true }
        );
    }
}
</script>



<style lang="scss" scoped>
.tournament-participants {
    width: 100%;

    .title {
        font-size: 32px !important;
        font-weight: bold;
        margin-bottom: 8px;
        color: var(--v-textPrimary-base);
        font-family: "Avenir", sans-serif !important;
    }

    .subtitle {
        font-size: 16px;
        color: var(--v-textSecondary-base);
        margin-bottom: 24px;
        font-family: "Avenir", sans-serif;
    }

    .info-card {
        background-color: var(--v-cardBackground-base);
        border: 1px solid #9A9A9A;
        border-radius: 10px;
        padding: 76px 60px 50px 60px;
        overflow-y: auto;

        .v-form {
            max-width: 720px;
            margin: auto;
        }

        .section {
            margin-bottom: 32px;

            .subtitle-2 {
                font-family: "Avenir", sans-serif !important;
            }

            .section-title {
                font-size: 20px;
                font-weight: 500;
                margin-bottom: 16px;
                color: var(--v-textPrimary-base);
            }

            .m-bttom{
                margin-bottom: 0px !important;
            }

            .radio-label {
                display: flex;
                flex-direction: column;
                color: var(--v-mainBlack-base);
                font-family: "Avenir", sans-serif;

                .subtitle-2 {
                    margin-top: 4px;
                    font-size: 12px;
                    font-family: "Avenir", sans-serif !important;
                }
            }
        }
    }

    ::v-deep .v-input__icon--append .v-icon {
        font-size: 14px;
        color: #9A9A9A;
    }

    ::v-deep .v-list--dense .v-list-item .v-list-item__title {
        font-size: 16px !important;
    }
}

::v-deep .v-input--switch {
    .v-input--selection-controls__input {
        .v-input--switch__track {
            height: 24px !important;
            width: 45px !important;
            top: calc(50% - 12px) !important;
            border-radius: 12px !important;
        }

        .v-input--switch__thumb {
            height: 20px !important;
            margin-left: 5px;
            width: 20px !important;
            top: calc(50% - 10px) !important;
        }
    }

    // Adjust margins for label alignment
    .v-input--selection-controls__input {
        margin-right: 18px;
    }

    // Adjust thumb position when switched on
    &.v-input--is-active {
        .v-input--switch__thumb {
            transform: translate(20px) !important;
        }

        // Add green color when active
        .v-input--switch__track {
            background-color: var(--v-mainGreen-base) !important;
            opacity: 1 !important;
        }
    }

    .v-input--selection-controls__label {
        color: var(--v-mainBlack-base) !important;
    }

    .v-label.theme--light {
        color: var(--v-mainBlack-base) !important;
    }
}

.platform-label {
    font-size: 16px;
    color: var(--v-textPrimary-base);
    font-family: "Avenir", sans-serif;
}

.timeline-container {
    padding: 20px 0;

    &.timeline-container--interactive {
        padding: 12px 0 16px;
        cursor: pointer;
        user-select: none;
        touch-action: none;
    }

    .timeline {
        position: relative;
        height: 100px;

        .timeline-base {
            position: absolute;
            width: 100%;
            height: 4px;
            background: #E0E0E0;
            top: 50%;
            transform: translateY(-50%);
        }

        .tournament-period {
            position: absolute;
            height: 15px;
            background: #B6B6B6;
            border: none;
            border-radius: 15px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1;
        }

        .registration-period {
            position: absolute;
            height: 15px;
            background: #28EA96;
            border: none;
            border-radius: 15px;
            top: 50%;
            transform: translateY(-50%);
            z-index: 2;
        }

        .period-label {
            position: absolute;
            font-size: 12px;
            white-space: nowrap;

            &.tournament {
                top: calc(100% + 35px); // Position below timeline and dates
                left: 50%;
                transform: translateX(-50%);
                color: #3B3B3B;
            }

            &.registration {
                bottom: calc(100% + 4px); // Position above timeline
                left: 50%;
                transform: translateX(-50%);
                color: #28EA96;
            }
        }

        .timeline-markers {
            position: absolute;
            width: 100%;
            height: 100%;
            z-index: 3; // Add this to make markers appear above everything

            .marker {
                position: absolute;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: var(--v-cardBackground-base); // Add this to ensure marker fills are visible
                border: 2px solid #9A9A9A;
                top: 50%;
                transform: translate(-50%, -50%);
                z-index: 3; // Add this to ensure markers are above the periods

                &.start {
                    left: 0;
                    color: #9A9A9A;
                    width: 0;
                    height: 0;
                    border: none;
                    background: transparent;
                }

                &.end {
                    right: 0;
                    transform: translate(50%, -50%);
                    color: #9A9A9A;
                    width: 0;
                    height: 0;
                    border: none;
                    background: transparent;

                    .date,
                    .label {
                        left: 50%;
                        transform: translateX(-50%);
                    }

                    .label {
                        top: calc(100% + 38px);
                    }
                }

                &.registration-end {
                    left: var(--registration-end-position);
                    border-color: #28EA96;
                }

                &.registration-handle {
                    width: 28px;
                    height: 28px;
                    background: transparent;
                    border: none;
                    cursor: grab;
                    z-index: 4;

                    &.registration-handle--dragging,
                    &:active {
                        cursor: grabbing;
                    }

                    &:focus {
                        outline: none;

                        .registration-handle__knob {
                            box-shadow: 0 0 0 3px rgba(40, 234, 150, 0.35);
                        }
                    }

                    &.registration-handle--closed {
                        .registration-handle__knob {
                            background: #FFFFFF;
                            border: none;
                        }
                    }

                    .registration-handle__knob {
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        width: 16px;
                        height: 16px;
                        transform: translate(-50%, -50%);
                        border-radius: 50%;
                        background: #FFFFFF;
                        border: none;
                        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
                    }
                }

                .date {
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    margin-top: 18px;
                    font-size: 12px;
                    color: var(--v-textSecondary-base);
                    white-space: nowrap;

                    &.registration-end-date {
                        bottom: calc(100% + 25px); // Move above the timeline
                        top: auto;
                        margin-top: 0;
                        color: var(--v-textSecondary-base);
                    }
                }

                .label {
                    position: absolute;
                    top: calc(100% + 38px);
                    transform: translateX(-50%);
                    font-size: 11px;
                    color: #9A9A9A;
                    white-space: nowrap;
                }
            }
        }

        .timeline-markers {
            // ...existing timeline-markers styles...

            .marker {
                // ...existing marker styles...

                .date {
                    // ...existing date styles...

                    &.registration-end-date {
                        left: -10px; // Add some spacing
                    }
                }
            }
        }
    }
}

.registration-timeline-editor {
    &__summary {
        font-family: "Avenir", sans-serif;
        text-align: center;
        color: var(--v-mainBlack-base);
        font-size: 14px;
        font-weight: 500;
        margin-top: 8px;
        margin-bottom: 0;
    }
}

.required-chip {
    background-color: var(--v-mainGreen-base) !important;
    color: white !important;
    border: none;
    font-size: 14px;
}

.unavailable-chip {
    // ...existing unavailable-chip styles...
}

// Ranking Modal Styling
::v-deep .ranking-modal {
    .v-card {
        background: var(--v-cardBackground-base) !important;
        border-radius: 10px;
        font-family: "Avenir", sans-serif;

        .ranking-modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 32px;
            border-bottom: 1px solid #e0e0e0;

            h3 {
                font-size: 24px;
                font-weight: bold;
                color: var(--v-mainBlack-base);
                margin: 0;
            }

            .v-btn {
                color: #9a9a9a;

                &:hover {
                    color: var(--v-mainBlack-base);
                }
            }
        }

        .ranking-modal-content {
            padding: 24px 32px 32px;

            .modal-description {
                font-size: 15px;
                color: var(--v-textSecondary-base);
                margin-bottom: 24px;
                line-height: 1.5;
            }

            .ranking-table-wrapper {
                overflow-x: auto;
                border: 1px solid #e0e0e0;
                border-radius: 8px;

                .ranking-comparison-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-family: "Avenir", sans-serif;

                    thead {
                        background: #f5f5f5;

                        tr th {
                            padding: 16px 20px;
                            text-align: left;
                            font-weight: 600;
                            font-size: 14px;
                            color: var(--v-mainBlack-base);
                            border-bottom: 2px solid #e0e0e0;

                            &:first-child {
                                width: 20%;
                            }

                            &:nth-child(2) {
                                width: 40%;
                            }

                            &:nth-child(3) {
                                width: 40%;
                            }
                        }
                    }

                    tbody {
                        tr {
                            border-bottom: 1px solid #e0e0e0;
                            transition: background-color 0.2s;

                            &:hover {
                                background: #fafafa;
                            }

                            &:last-child {
                                border-bottom: none;
                            }

                            td {
                                padding: 16px 20px;
                                vertical-align: top;

                                &.ranking-name {
                                    font-weight: 600;
                                    color: var(--v-mainBlack-base);
                                    font-size: 14px;
                                }

                                &.ranking-description {
                                    color: var(--v-textSecondary-base);
                                    font-size: 14px;
                                    line-height: 1.5;
                                }

                                &.ranking-formula {
                                    font-family: "Courier New", monospace;
                                    font-size: 13px;
                                    color: var(--v-textPrimary-base);
                                    background: #f9f9f9;
                                    border-radius: 4px;
                                    padding: 12px 16px !important;

                                    .formula-single {
                                        white-space: pre-wrap;
                                    }

                                    .formula-dual {
                                        div {
                                            margin-bottom: 6px;

                                            &:last-child {
                                                margin-bottom: 0;
                                            }

                                            strong {
                                                color: var(--v-mainGreen-base);
                                                font-family: "Avenir", sans-serif;
                                                font-size: 12px;
                                                margin-right: 6px;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@media (max-width: 599px) {
    .participant-settings {
        .title { font-size: 22px !important; }
        .subtitle { font-size: 14px; }

        .info-card {
            padding: 24px 16px 24px 16px;
        }
    }

    .registration-timeline-editor {
        &__summary {
            text-align: left;
        }

        .timeline-container--interactive {
            padding-bottom: 36px;
        }
    }

    // Ranking modal — mobile card layout
    ::v-deep .ranking-modal {
        .v-card {
            .ranking-modal-header {
                padding: 16px 16px 14px;
                // Override Vuetify v-card-title flex-wrap so the X stays on the same line as the title
                flex-wrap: nowrap !important;
                align-items: center !important;

                h3 {
                    font-size: 16px;
                    flex: 1;
                    min-width: 0;
                    padding-right: 8px;
                    white-space: normal;
                    word-break: break-word;
                }
            }

            .ranking-modal-content {
                padding: 12px 16px 20px;
            }
        }

        .ranking-table-wrapper {
            border: none;
            border-radius: 0;
            overflow-x: visible;
        }

        .ranking-comparison-table {
            thead { display: none; }

            tbody tr {
                display: flex;
                flex-direction: column;
                padding: 14px 16px;
                margin-bottom: 10px;
                background: #fafafa;
                border: 1px solid #e8e8e8;
                border-radius: 10px;
                border-bottom: 1px solid #e8e8e8 !important;

                &:last-child { margin-bottom: 0; }
            }

            tbody tr td {
                display: block;
                padding: 0 !important;
                border: none;

                &.ranking-name {
                    font-size: 14px;
                    font-weight: 700;
                    margin-bottom: 6px;
                    color: var(--v-mainBlack-base);
                }

                &.ranking-description {
                    font-size: 13px;
                    line-height: 1.55;
                    margin-bottom: 8px;
                    color: var(--v-textSecondary-base);
                }

                &.ranking-formula {
                    font-size: 11px;
                    padding: 8px 10px !important;
                    border-radius: 6px;
                    background: #f0f0f0;
                    margin-top: 2px;
                }
            }
        }
    }
}

</style>
